import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Tooltip } from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  RadioButtonUnchecked as UncheckedIcon,
  AutoFixHigh as WizardIcon,
  Help as QuestionsIcon,
  PlayArrow as InProgressIcon,
  Note as NoteIcon,
} from '@mui/icons-material';
import { useUpdatePropertyMutation } from '../store/docApi';
import { PROGRESS_STATE, PROGRESS_KEY } from '../constants/progress';
import ProjectMaker from './ProjectMaker';
import Markdown from '../components/widgets/Markdown';

const StepItem = ({ step, stepId, pattern, progress, onProgressUpdate, onActionClick }) => {
  const stepProgress = progress?.[PROGRESS_KEY.STEP(stepId)] || PROGRESS_STATE.NOT_STARTED;
  const isCompleted = stepProgress === PROGRESS_STATE.COMPLETED;
  const isInProgress = stepProgress === PROGRESS_STATE.IN_PROGRESS;

  // Find associated part if part_id exists and merge its properties into step
  const mergedStep = step.part_id && pattern?.parts[step.part_id]
    ? { ...step, ...pattern.parts[step.part_id] }
    : step;

  const handleStepClick = () => {
    if (onProgressUpdate) {
      // Cycle through states: not started -> in progress -> completed -> not started
      const newState = (stepProgress + 1) % 3;
      onProgressUpdate(PROGRESS_KEY.STEP(stepId), newState);
    }
  };

  const getProgressIcon = () => {
    if (isCompleted) {
      return <CheckCircleIcon color="success" />;
    } else if (isInProgress) {
      return <InProgressIcon color="primary" />;
    } else {
      return <UncheckedIcon color="disabled" />;
    }
  };

  const getActionIcons = (step) => {
    const actions = [];
    
    if (step.withInstructions) {
      actions.push(
        <Tooltip key="instructions" title="With Instructions">
          <WizardIcon 
            fontSize="small" 
            color="primary" 
            sx={{ opacity: 0.7, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(step, stepId);
            }}
          />
        </Tooltip>
      );
    }
    
    if (step.questions) {
      actions.push(
        <Tooltip key="questions" title="With Questions">
          <QuestionsIcon 
            fontSize="small" 
            color="primary" 
            sx={{ opacity: 0.7, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(step, stepId);
            }}
          />
        </Tooltip>
      );
    }
    
    if (step.withNotes) {
      actions.push(
        <Tooltip key="notes" title="With Notes">
          <NoteIcon 
            fontSize="small" 
            color="primary" 
            sx={{ opacity: 0.7, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(step, stepId);
            }}
          />
        </Tooltip>
      );
    }

    return actions.length > 0 ? (
      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
        {actions}
      </Box>
    ) : null;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: 1,
          mb: 1,
          p: 1,
          pl: stepId.split('_').length * 4 - 4,
          cursor: onProgressUpdate ? 'pointer' : 'default',
          '&:hover': onProgressUpdate ? {
            bgcolor: 'action.hover'
          } : {}
        }}
        onClick={handleStepClick}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 40 }}>
          {getProgressIcon()}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography component="span" variant="body1">
              {mergedStep.title || mergedStep.name || `Step ${stepId.split('_').pop()}`}
            </Typography>
            {isInProgress && (
              <Chip 
                label="In Progress" 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
            {getActionIcons(mergedStep)}
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ ml: 'auto', opacity: 0.7 }}
            >
              {stepId}
            </Typography>
          </Box>
          {mergedStep.description && (
            <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: mergedStep.options?.length ? 1 : 0 }}>
              <Markdown text={mergedStep.description} />
            </Typography>
          )}
          {mergedStep.options && mergedStep.options.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {mergedStep.options.map((option, optionIndex) => (
                <Chip
                  key={optionIndex}
                  label={option.name || option.title || `Option ${optionIndex + 1}`}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onProgressUpdate) {
                      onProgressUpdate(PROGRESS_KEY.OPTION(stepId), optionIndex);
                    }
                  }}
                  sx={{ 
                    bgcolor: progress?.[PROGRESS_KEY.OPTION(stepId)] === optionIndex ? 'primary.light' : 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {!mergedStep.part_id && mergedStep.steps && mergedStep.steps.map((subStep, subIndex) => (
        <StepItem
          key={`${stepId}_${subIndex}`}
          step={subStep}
          stepId={`${stepId}_${subIndex}`}
          pattern={pattern}
          progress={progress}
          onProgressUpdate={onProgressUpdate}
          onActionClick={onActionClick}
        />
      ))}
    </>
  );
};

export default function ProjectOverview({ pattern, item, connectionIndex }) {
  const [updateProperty] = useUpdatePropertyMutation();
  const [selectedStep, setSelectedStep] = useState(null);

  if (!pattern || !pattern.steps) return null;

  const handleProgressUpdate = async (stepId, value = PROGRESS_STATE.NOT_STARTED) => {
    if (!item || connectionIndex === undefined) return;
    
    await updateProperty({
      label: 'project',
      pk: item.pk,
      id: item.id,
      name: `connections/${connectionIndex}/progress/${stepId}`,
      value: value
    });
  };

  const handleActionClick = (step, stepId) => {
    // Find the top-level index for this step
    const findTopLevelIndex = (steps, targetStep, currentPath = '') => {
      for (let i = 0; i < steps.length; i++) {
        const stepId = currentPath ? `${currentPath}_${i}` : `${i}`;
        if (steps[i] === targetStep) {
          return i;
        }
        if (steps[i].steps) {
          const result = findTopLevelIndex(steps[i].steps, targetStep, stepId);
          if (result !== null) return result;
        }
      }
      return null;
    };

    const topLevelIndex = findTopLevelIndex(pattern.steps, step);
    setSelectedStep({ ...step, stepId, stepIndex: topLevelIndex });
  };

  if (selectedStep) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {selectedStep.title || selectedStep.name || 'Step Details'}
        </Typography>
        <ProjectMaker
          item={item}
          pattern={pattern}
          currentStep={selectedStep}
          connectionIndex={connectionIndex}
          onStepChange={() => {
            // Handle step change if needed
          }}
        />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Pattern Overview
      </Typography>
      <Box>
        {pattern.steps.map((step, index) => (
          <StepItem
            key={index}
            step={step}
            stepId={index.toString()}
            pattern={pattern}
            progress={item?.connections?.[connectionIndex]?.progress}
            onProgressUpdate={handleProgressUpdate}
            onActionClick={handleActionClick}
          />
        ))}
      </Box>
    </Paper>
  );
} 